/* Publieke onboarding-landingspagina — bereikbaar via /onboarding/:token uit
 * de e-mail die de super-admin verstuurt. Verifieert token, toont warme
 * welkomstpagina en start dan de wizard. */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, AlertTriangle, Clock, Building2 } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicOnboardingPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, invite: null, error: null });
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BASE}/api/public/invitations/${token}`);
        setState({ loading: false, invite: r.data, error: null });
      } catch (e) {
        const detail = e.response?.data?.detail || "Uitnodiging niet geldig";
        const httpCode = e.response?.status;
        setState({ loading: false, invite: null,
                    error: { message: detail, code: httpCode } });
      }
    })();
  }, [token]);

  const start = async () => {
    setStarting(true);
    try {
      await axios.post(`${BASE}/api/public/invitations/${token}/start`);
      // Bewaar token in sessionStorage zodat de wizard hem kan gebruiken
      sessionStorage.setItem("onboarding_token", token);
      sessionStorage.setItem("onboarding_org_name", state.invite.org_name);
      navigate("/onboarding-start");
    } catch (e) {
      setState(s => ({ ...s, error: { message: "Kon niet starten. Probeer opnieuw.", code: null } }));
    }
    setStarting(false);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-neutral-500">Uitnodiging laden…</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-6">
        <Card className="max-w-lg p-8 text-center">
          <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
          <h2 className="text-2xl font-medium mb-2">Uitnodiging niet beschikbaar</h2>
          <p className="text-neutral-600 mb-4">{state.error.message}</p>
          {state.error.code === 410 && (
            <p className="text-sm text-neutral-500">
              Neem contact op met uw contactpersoon voor een nieuwe uitnodiging.
            </p>
          )}
        </Card>
      </div>
    );
  }

  const inv = state.invite;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-sky-50 py-12 px-6">
      <div className="max-w-2xl mx-auto">
        <Card className="overflow-hidden shadow-lg" data-testid="public-onboarding-card">
          <div className="p-8 text-white" style={{ background: "linear-gradient(135deg, #2D4A3E, #3d6152)" }}>
            <div className="flex items-center gap-3 mb-3">
              <Building2 className="h-8 w-8" />
              <span className="text-sm uppercase tracking-widest opacity-80">Onboarding uitnodiging</span>
            </div>
            <h1 className="text-3xl font-medium mb-2">Van harte welkom, {inv.contact_naam}</h1>
            <p className="opacity-90">Fijn dat <strong>{inv.org_name}</strong> voor ons platform kiest.</p>
          </div>

          <div className="p-8 space-y-5">
            {inv.welkomsttekst && (
              <div className="border-l-4 border-sage-500 pl-4 italic text-neutral-700"
                   style={{ borderColor: "#2D4A3E" }}>
                {inv.welkomsttekst}
              </div>
            )}
            <div>
              <h3 className="font-medium mb-2">Wat staat u te wachten?</h3>
              <ul className="space-y-2 text-neutral-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-1 flex-shrink-0" />
                  <span>Uw organisatiegegevens (KVK, adres, contact) invoeren</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-1 flex-shrink-0" />
                  <span>Uw opvanglocaties met LRK-nummers en capaciteit</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-1 flex-shrink-0" />
                  <span>Groepen per locatie met leeftijden en capaciteit</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-1 flex-shrink-0" />
                  <span>Tekenbevoegden voor contracten</span>
                </li>
              </ul>
            </div>

            <div className="bg-neutral-50 rounded-lg p-4 flex items-center gap-3">
              <Clock className="h-5 w-5 text-neutral-500 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium">Ongeveer 15–25 minuten</div>
                <div className="text-xs text-neutral-500">
                  U kunt uw sessie op ieder moment onderbreken — voortgang wordt automatisch opgeslagen.
                </div>
              </div>
            </div>

            <div className="text-xs text-neutral-500 border-t pt-3">
              Uitnodiging geldig tot <strong>{inv.vervaldatum}</strong>.
              Status: <span className="uppercase tracking-widest">{inv.status}</span>
            </div>

            <div className="flex justify-end">
              <Button onClick={start} disabled={starting} size="lg"
                      data-testid="public-onboarding-start"
                      style={{ background: "#2D4A3E" }}
                      className="text-white text-base px-8">
                {starting ? "Bezig…" : "Start onboarding →"}
              </Button>
            </div>
          </div>
        </Card>
        <p className="text-center text-xs text-neutral-500 mt-6">
          Powered by Webster · Kinderopvang slim geregeld
        </p>
      </div>
    </div>
  );
}
